import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Event, EventPublishState, Member } from '../../types';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Edit2,
  Truck,
  EyeOff,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  Trash2,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../lib/utils';
import { useApiJson } from '../../hooks/useApi';
import {
  addEventParticipant,
  createEvent,
  deleteEvent,
  deleteEventCardImage,
  deleteEventFlyer,
  removeEventParticipant,
  reorderEvent,
  updateEvent,
  type EventMediaChange,
  type EventWritePayload,
} from '../../lib/events-api';
import { groupVisitorEvents, moveWithinGroup, type ReorderDirection } from '../../lib/event-order';
import { eventScheduleLine, PUBLISH_STATE_LABELS } from '../../lib/event-utils';
import { eventModules, eventSummaryChips } from '../../lib/event-modules';
import {
  EventEditorForm,
  editorStateFromEvent,
  emptyEditorState,
} from './EventEditorForm';
import { EventRegistrationsPanel } from './EventRegistrationsPanel';
import { EventPickupRequestsPanel } from './EventPickupRequestsPanel';
import { MarkdownContent } from '../markdown/MarkdownContent';
import { useAuth } from '../AuthProvider';
import { eventDeletedToast, pushScoringToast } from '../common/admin-toasts';
import { usePinDelete } from '../common/PinDeleteDialog';

interface EventsDto {
  ok: boolean;
  events: Event[];
}

interface MembersDto {
  ok: boolean;
  members: Member[];
}

type Mode = 'list' | 'create' | 'edit' | 'registrations' | 'pickups';
type ListView = 'active' | 'past';

const PUBLISH_BADGE: Record<EventPublishState, string> = {
  draft: 'bg-[var(--color-warning-subtle)] text-[var(--color-text-primary)]',
  published: 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]',
  archived: 'bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]',
};

function MemberRosterPicker({
  event,
  members,
  onChanged,
}: {
  event: Event;
  members: Member[];
  onChanged: () => Promise<void>;
}) {
  const [query, setQuery] = useState('');
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [removedUids, setRemovedUids] = useState<Set<string>>(new Set());
  const pinDelete = usePinDelete();

  const rosterUids = new Set((event.participants ?? []).map((p) => p.uid));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members.slice(0, 8);
    return members
      .filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.email.toLowerCase().includes(q) ||
          (m.lmNumber?.toLowerCase().includes(q) ?? false),
      )
      .slice(0, 8);
  }, [members, query]);

  const handleAdd = async (uid: string) => {
    setBusyUid(uid);
    try {
      await addEventParticipant(event.id, uid);
      await onChanged();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyUid(null);
    }
  };

  const handleRemove = (uid: string, name: string) => {
    pinDelete.ask({
      title: `Remove ${name} from the roster?`,
      description: 'They stay on the member list. This only takes them off this event.',
      confirmLabel: 'Remove',
      busyLabel: 'Removing…',
      run: async (pin) => {
        setBusyUid(uid);
        try {
          await removeEventParticipant(event.id, uid, pin);
          setRemovedUids((prev) => new Set(prev).add(uid));
          pushScoringToast(`${name} removed from the roster`);
          void onChanged();
        } finally {
          setBusyUid(null);
        }
      },
    });
  };

  const participants = (event.participants ?? []).filter((person) => !removedUids.has(person.uid));

  return (
    <div className="mt-4 pt-4 border-t border-[var(--color-border)] space-y-3">
      {pinDelete.dialog}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-[var(--color-text-secondary)]">
          Roster ({participants.length}) · showing {event.participantCount} publicly
        </p>
      </div>

      {participants.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {participants.map((p) => (
            <span
              key={p.uid}
              className="inline-flex items-center gap-1.5 text-xs bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] px-2.5 py-1 rounded-full"
            >
              {p.name}
              <button
                type="button"
                title="Remove from roster"
                disabled={busyUid === p.uid}
                onClick={() => handleRemove(p.uid, p.name)}
                className="hover:text-[var(--color-error)] transition-colors"
              >
                <UserMinus className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
        <input
          type="search"
          placeholder="Search members to add…"
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') ev.preventDefault();
          }}
          className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 pl-9 pr-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
        />
      </div>

      <div className="space-y-1">
        {filtered.map((member) => {
          const onRoster = rosterUids.has(member.uid);
          return (
            <div
              key={member.uid}
              className="flex items-center justify-between gap-3 py-2 px-2 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-2)]"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                  {member.name}
                </p>
                <p className="text-xs text-[var(--color-text-tertiary)] truncate">{member.email}</p>
              </div>
              {onRoster ? (
                <span className="text-xs text-[var(--color-success-text)] shrink-0">On roster</span>
              ) : (
                <button
                  type="button"
                  disabled={busyUid === member.uid}
                  onClick={() => void handleAdd(member.uid)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] shrink-0"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Add
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function clearRegistrationParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('regs');
  next.delete('pickups');
  next.delete('view');
  next.delete('q');
  next.delete('paymentStatus');
  next.delete('memberType');
  next.delete('status');
  next.delete('sort');
  return next;
}

export function EventsManagement() {
  const { isAdmin, isOrganizer } = useAuth();
  const readOnly = isOrganizer && !isAdmin;
  const { data, mutate } = useApiJson<EventsDto>('/api/events');
  const { data: membersData } = useApiJson<MembersDto>('/api/members', { enabled: !readOnly });
  const [searchParams, setSearchParams] = useSearchParams();
  const regsParam = searchParams.get('regs');
  const pickupsParam = searchParams.get('pickups');

  const [mode, setMode] = useState<Mode>(pickupsParam ? 'pickups' : regsParam ? 'registrations' : 'list');
  const [selectedId, setSelectedId] = useState<string | null>(pickupsParam ?? regsParam);
  const [listView, setListView] = useState<ListView>('active');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [removedEventIds, setRemovedEventIds] = useState<Set<string>>(new Set());
  const [orderOverride, setOrderOverride] = useState<Record<string, number> | null>(null);
  const [reorderBusy, setReorderBusy] = useState(false);
  const pinDelete = usePinDelete();

  const allEvents = useMemo(
    () =>
      (data?.events ?? [])
        .filter((event) => !removedEventIds.has(event.id))
        .map((event) =>
          orderOverride && event.id in orderOverride
            ? { ...event, displayOrder: orderOverride[event.id] }
            : event,
        ),
    [data?.events, removedEventIds, orderOverride],
  );
  const activeGroups = useMemo(
    () => groupVisitorEvents(allEvents.filter((event) => event.publishState !== 'archived')),
    [allEvents],
  );
  const archivedEvents = useMemo(
    () =>
      allEvents
        .filter((event) => event.publishState === 'archived')
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [allEvents],
  );
  const events =
    listView === 'past'
      ? archivedEvents
      : [...activeGroups.open, ...activeGroups.closed, ...activeGroups.past];
  const counts = useMemo(
    () => ({
      active: allEvents.filter((event) => event.publishState !== 'archived').length,
      past: allEvents.filter((event) => event.publishState === 'archived').length,
    }),
    [allEvents],
  );

  const members = membersData?.members ?? [];
  const selectedEvent = selectedId ? allEvents.find((e) => e.id === selectedId) : undefined;

  useEffect(() => {
    if (readOnly && pickupsParam) {
      setMode('list');
      setSelectedId(null);
      return;
    }
    if (pickupsParam) {
      setSelectedId(pickupsParam);
      setMode('pickups');
      return;
    }
    if (regsParam) {
      setSelectedId(regsParam);
      setMode('registrations');
      return;
    }
    setMode((current) => (current === 'registrations' || current === 'pickups' ? 'list' : current));
  }, [regsParam, pickupsParam, readOnly]);

  const refresh = async () => {
    await mutate();
  };

  const backToList = () => {
    setMode('list');
    setSelectedId(null);
    setSearchParams((prev) => clearRegistrationParams(prev));
  };

  const openRegistrations = (eventId: string) => {
    setSelectedId(eventId);
    setMode('registrations');
    setSearchParams((prev) => {
      const next = clearRegistrationParams(prev);
      next.set('regs', eventId);
      return next;
    });
  };

  const openPickups = (eventId: string) => {
    if (readOnly) return;
    setSelectedId(eventId);
    setMode('pickups');
    setSearchParams((prev) => {
      const next = clearRegistrationParams(prev);
      next.set('pickups', eventId);
      return next;
    });
  };

  const registrationsView = searchParams.get('view') === 'registrations';
  useEffect(() => {
    if (!registrationsView || regsParam || pickupsParam || allEvents.length === 0) return;
    const first = [...allEvents].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    )[0];
    if (!first) return;
    setSelectedId(first.id);
    setMode('registrations');
    setSearchParams((prev) => {
      const next = clearRegistrationParams(prev);
      next.set('regs', first.id);
      return next;
    });
  }, [registrationsView, regsParam, pickupsParam, allEvents, setSearchParams]);

  const handleCreate = async (payload: EventWritePayload, media: EventMediaChange) => {
    await createEvent(payload, media);
    backToList();
    await refresh();
  };

  const handleUpdate = async (payload: EventWritePayload, media: EventMediaChange) => {
    if (!selectedId) return;
    if (media.flyer.remove && !media.flyer.file) await deleteEventFlyer(selectedId);
    if (media.card.remove && !media.card.file) await deleteEventCardImage(selectedId);
    await updateEvent(selectedId, payload, media);
    backToList();
    await refresh();
  };

  const handlePublishState = async (event: Event, publishState: EventPublishState) => {
    setBusyId(event.id);
    setListError(null);
    try {
      await updateEvent(event.id, { publishState });
      await refresh();
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Could not update event');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = (event: Event) => {
    const regs = event.registrationCount ?? 0;
    const pickups = event.pickupRequestCount ?? 0;
    const description =
      regs > 0 || pickups > 0
        ? `Its ${regs} registration${regs === 1 ? '' : 's'}${
            pickups > 0 ? ` and ${pickups} pickup request${pickups === 1 ? '' : 's'}` : ''
          } and payment screenshots will be deleted too. Consider archiving instead.`
        : 'This cannot be undone.';
    pinDelete.ask({
      title: `Delete "${event.title}"?`,
      description,
      confirmLabel: 'Delete event',
      run: async (pin) => {
        await deleteEvent(event.id, pin);
        setRemovedEventIds((prev) => new Set(prev).add(event.id));
        if (selectedId === event.id) backToList();
        pushScoringToast(eventDeletedToast(event.title));
        void refresh();
      },
    });
  };

  const handleReorder = async (event: Event, direction: ReorderDirection) => {
    if (readOnly || reorderBusy) return;
    const group = activeGroups.open.some((item) => item.id === event.id)
      ? activeGroups.open
      : activeGroups.closed;
    const moved = moveWithinGroup(group, event.id, direction);
    if (!moved.ok) return;
    const next: Record<string, number> = {};
    for (const [id, order] of moved.orders) next[id] = order;
    setOrderOverride(next);
    setReorderBusy(true);
    try {
      await reorderEvent(event.id, direction);
      await refresh();
      setOrderOverride(null);
    } catch (err) {
      setOrderOverride(null);
      pushScoringToast(err instanceof Error ? err.message : 'Could not save the new order');
    } finally {
      setReorderBusy(false);
    }
  };

  const moveLimits = new Map<string, { up: boolean; down: boolean }>();
  for (const group of [activeGroups.open, activeGroups.closed]) {
    group.forEach((event, index) => {
      moveLimits.set(event.id, { up: index > 0, down: index < group.length - 1 });
    });
  }
  const showGroupLabels = activeGroups.open.length > 0 && activeGroups.closed.length > 0;
  const sections =
    listView === 'past'
      ? [{ key: 'archived', label: null as string | null, rows: events }]
      : [
          {
            key: 'open',
            label: showGroupLabels ? 'Open for registration' : null,
            rows: activeGroups.open,
          },
          {
            key: 'closed',
            label: showGroupLabels ? 'Upcoming, registration closed' : null,
            rows: activeGroups.closed,
          },
          { key: 'rest', label: null, rows: activeGroups.past },
        ].filter((section) => section.rows.length > 0);

  const iconButton =
    'inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-tertiary)] transition-colors disabled:opacity-50';

  return (
    <div className="space-y-4">
      {pinDelete.dialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {mode === 'list' ? (
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Active or past events">
            {(['active', 'past'] as const).map((view) => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={listView === view}
                onClick={() => setListView(view)}
                className={cn(
                  'px-3 py-1.5 rounded-[var(--radius-full)] text-xs font-medium transition-colors min-h-11',
                  listView === view
                    ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]',
                )}
              >
                {view === 'active' ? 'Active' : 'Past'}{' '}
                <span className="tabular-nums opacity-70">{counts[view]}</span>
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        {mode === 'list' && !readOnly ? (
          <button
            type="button"
            onClick={() => {
              setMode('create');
              setSelectedId(null);
              setSearchParams((prev) => clearRegistrationParams(prev));
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] font-medium text-sm bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Event
          </button>
        ) : mode !== 'list' ? (
          <button
            type="button"
            onClick={backToList}
            className="flex items-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] font-medium text-sm bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] transition-colors"
          >
            <X className="w-4 h-4" />
            Back to list
          </button>
        ) : null}
      </div>

      <AnimatePresence mode="wait">
        {mode === 'create' && !readOnly && (
          <motion.div
            key="create"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <EventEditorForm
              initial={emptyEditorState()}
              isCreate
              submitLabel="Create Event"
              onSubmit={handleCreate}
              onCancel={backToList}
            />
          </motion.div>
        )}

        {mode === 'edit' && selectedEvent && !readOnly && (
          <motion.div
            key={`edit-${selectedEvent.id}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <EventEditorForm
              initial={editorStateFromEvent(selectedEvent)}
              isCreate={false}
              eventId={selectedEvent.id}
              submitLabel="Save Changes"
              rosterCount={selectedEvent.participants?.length ?? 0}
              roster={<MemberRosterPicker event={selectedEvent} members={members} onChanged={refresh} />}
              onSubmit={handleUpdate}
              onCancel={backToList}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {mode === 'registrations' && !data && (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-tertiary)]" />
        </div>
      )}

      {mode === 'registrations' && data && !selectedEvent && (
        <p className="text-sm text-[var(--color-text-secondary)]">That event is no longer in the list.</p>
      )}

      {mode === 'registrations' && selectedEvent && (
        <div key={selectedEvent.id} className="contents">
          <EventRegistrationsPanel
            readOnly={readOnly}
            event={selectedEvent}
            events={allEvents}
            onChanged={refresh}
            onSelectEvent={openRegistrations}
          />
        </div>
      )}

      {mode === 'pickups' && !readOnly && !data && (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-tertiary)]" />
        </div>
      )}

      {mode === 'pickups' && !readOnly && data && !selectedEvent && (
        <p className="text-sm text-[var(--color-text-secondary)]">That event is no longer in the list.</p>
      )}

      {mode === 'pickups' && !readOnly && selectedEvent && (
        <div key={`pickups-${selectedEvent.id}`} className="contents">
          <EventPickupRequestsPanel
            event={selectedEvent}
            events={allEvents}
            onChanged={refresh}
            onSelectEvent={openPickups}
          />
        </div>
      )}

      {mode === 'list' && (
        <div className="space-y-3">
          {listError && (
            <p role="alert" className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2">
              {listError}
            </p>
          )}
          {sections.map((section) => (
            <div key={section.key} className="space-y-3">
              {section.label && (
                <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
                  {section.label}
                </h3>
              )}
              {section.rows.map((event) => {
            const summary = event.registrationSummary;
            const busy = busyId === event.id;
            const arrows = !readOnly && listView === 'active' ? moveLimits.get(event.id) : undefined;
            const modules = eventModules(event);
            const chips = eventSummaryChips(event);
            const registrationCount = summary?.total ?? event.registrationCount ?? 0;
            const pending = summary?.pending ?? 0;
            const pickupCount = event.pickupRequestCount ?? 0;
            const showRegistrations = modules.registration || registrationCount > 0;
            const showPickups = !readOnly && (event.pickupEnabled === true || pickupCount > 0);
            return (
              <div
                key={event.id}
                className="bg-[var(--color-surface-1)] p-4 rounded-[var(--radius-lg)] border border-[var(--color-border)]"
              >
                <div className="flex justify-between items-start gap-3">
                  {(arrows?.up || arrows?.down) && (
                    <div className="flex shrink-0 gap-0.5">
                      {arrows.up && (
                        <button
                          type="button"
                          disabled={reorderBusy}
                          onClick={() => void handleReorder(event, 'up')}
                          className={cn(iconButton, 'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]')}
                          title="Move up"
                          aria-label={`Move ${event.title} up`}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                      )}
                      {arrows.down && (
                        <button
                          type="button"
                          disabled={reorderBusy}
                          onClick={() => void handleReorder(event, 'down')}
                          className={cn(iconButton, 'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]')}
                          title="Move down"
                          aria-label={`Move ${event.title} down`}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{event.title}</h3>
                      <span
                        className={cn(
                          'text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-[var(--radius-sm)]',
                          PUBLISH_BADGE[event.publishState],
                        )}
                      >
                        {PUBLISH_STATE_LABELS[event.publishState]}
                      </span>
                      {event.visibility === 'members' && (
                        <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-[var(--radius-sm)] bg-[var(--color-info-subtle)] text-[var(--color-text-secondary)]">
                          Members only
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                      {eventScheduleLine(event.date, event.location)}
                    </p>
                    {chips.length > 0 && (
                      <ul className="mt-2 flex flex-wrap gap-1.5" aria-label={`Summary for ${event.title}`}>
                        {chips.map((chip) => (
                          <li
                            key={chip}
                            className="rounded-[var(--radius-full)] bg-[var(--color-surface-2)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-secondary)]"
                          >
                            {chip}
                          </li>
                        ))}
                      </ul>
                    )}
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs font-medium text-[var(--color-text-secondary)]">
                        Details
                      </summary>
                      {event.description?.trim() ? (
                        <MarkdownContent
                          text={event.description}
                          className="text-xs text-[var(--color-text-secondary)] mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">No description.</p>
                      )}
                      <p className="text-xs text-[var(--color-text-tertiary)] mt-1 font-mono">
                        /{event.slug} · {event.regPrefix ?? 'EVT'}-0001
                      </p>
                    </details>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {showRegistrations && (
                      <button
                        type="button"
                        onClick={() => openRegistrations(event.id)}
                        className={cn(iconButton, 'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-accent)]')}
                        title="Registrations"
                        aria-label={`Registrations for ${event.title}`}
                      >
                        <ClipboardList className="w-4 h-4" />
                        {registrationCount > 0 && (
                          <span className="ml-0.5 text-[10px] tabular-nums font-medium">{registrationCount}</span>
                        )}
                      </button>
                    )}
                    {pending > 0 && (
                      <span className="rounded-[var(--radius-full)] bg-[var(--color-warning-subtle)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-text-primary)]">
                        {pending} to verify
                      </span>
                    )}
                    {showPickups && (
                      <button
                        type="button"
                        onClick={() => openPickups(event.id)}
                        className={cn(iconButton, 'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-accent)]')}
                        title="Pickup requests"
                        aria-label={`Pickup requests for ${event.title}`}
                      >
                        <Truck className="w-4 h-4" />
                        {pickupCount > 0 && (
                          <span className="ml-0.5 text-[10px] tabular-nums font-medium">{pickupCount}</span>
                        )}
                      </button>
                    )}
                    {!readOnly && event.publishState === 'draft' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handlePublishState(event, 'published')}
                        className={cn(iconButton, 'hover:bg-[var(--color-success-subtle)] hover:text-[var(--color-success-text)]')}
                        title="Publish"
                        aria-label={`Publish ${event.title}`}
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {!readOnly && event.publishState === 'published' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handlePublishState(event, 'draft')}
                        className={cn(iconButton, 'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text-primary)]')}
                        title="Unpublish (back to draft)"
                        aria-label={`Unpublish ${event.title}`}
                      >
                        <EyeOff className="w-4 h-4" />
                      </button>
                    )}
                    {!readOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(event.id);
                        setMode('edit');
                      }}
                      className={cn(iconButton, 'hover:bg-[var(--color-surface-2)] hover:text-[var(--color-accent)]')}
                      title="Edit event"
                      aria-label={`Edit ${event.title}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    )}
                    {!readOnly && (
                      <details className="relative">
                        <summary
                          aria-label={`More actions for ${event.title}`}
                          className={cn(iconButton, 'cursor-pointer list-none hover:bg-[var(--color-surface-2)] [&::-webkit-details-marker]:hidden')}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </summary>
                        <div className="absolute right-0 z-20 mt-1 min-w-36 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] py-1 shadow-[var(--shadow-md)]">
                          {event.publishState === 'published' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void handlePublishState(event, 'archived')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] disabled:opacity-50"
                              aria-label={`Archive ${event.title}`}
                            >
                              <Archive className="w-4 h-4" />
                              Archive
                            </button>
                          )}
                          {event.publishState === 'archived' && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void handlePublishState(event, 'published')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] disabled:opacity-50"
                              aria-label={`Unarchive ${event.title}`}
                            >
                              <ArchiveRestore className="w-4 h-4" />
                              Unarchive
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void handleDelete(event)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-error)] hover:bg-[var(--color-error-subtle)] disabled:opacity-50"
                            aria-label={`Delete ${event.title}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            );
              })}
            </div>
          ))}

          {events.length === 0 && (
            <p className="text-sm text-[var(--color-text-tertiary)] text-center py-8">
              {allEvents.length === 0
                ? readOnly
                  ? 'No events yet.'
                  : 'No events yet. Create the first one above.'
                : listView === 'past'
                  ? 'No archived events.'
                  : 'No active events.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
